import { FC, memo, useEffect, useMemo } from 'react';
import { PickingList } from 'src/entities/pickingListEntity';
import { useQueryPickingList } from 'src/hooks/useQueryPickingList';

import LoadingComponent from './LoadingComponent';

export type PresenterProps = {
  models: PickingList[];
  date: string;
  unit: string;
  barcodeShippers: string[];
}

type Props = {
  shiftIds: number[];
  date: string;
  presenter: FC<PresenterProps>;
  unit: string;
  onCountChange?: (count: number) => void;
  initialSelectedShippers?: string[];
  initialBarcodeShippers?: string[];
}

export function computePickingPageCount(models: PickingList[]): number {
  return models.reduce((total, course) => {
    if (course.dispatches.length === 0) return total + 1;
    const courseCount = course.dispatches.reduce((dispatchTotal, loadingDate) => {
      const dateCount = loadingDate.shippers.reduce(
        (shipperTotal, shipper) => shipperTotal + shipper.unloads.length, 0
      );
      return dispatchTotal + dateCount;
    }, 0);
    return total + courseCount;
  }, 0);
}

export function buildPickingShipperOptions(models: PickingList[]): string[] {
  const names = new Set<string>();
  models.forEach((course) => {
    course.dispatches.forEach((dispatch) => {
      dispatch.shippers.forEach((shipper) => {
        if (shipper.shipper_name) names.add(shipper.shipper_name);
      });
    });
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b, 'ja'));
}

export function filterPickingListsByShippers(models: PickingList[], selectedShippers: string[]): PickingList[] {
  if (selectedShippers.length === 0) return models;

  const selected = new Set(selectedShippers);
  return models
    .map((course) => ({
      ...course,
      dispatches: course.dispatches
        .map((dispatch) => ({
          ...dispatch,
          shippers: dispatch.shippers.filter((shipper) => selected.has(shipper.shipper_name)),
        }))
        .filter((dispatch) => dispatch.shippers.length > 0),
    }))
    .filter((course) => course.dispatches.length > 0);
}

const PickingListComponent: FC<Props> = memo(({
  shiftIds,
  date,
  presenter: Presenter,
  unit,
  onCountChange,
  initialSelectedShippers = [],
  initialBarcodeShippers = [],
}) => {
  const pickingQuery = useQueryPickingList(shiftIds);

  const filteredPickingLists = useMemo(
    () => (pickingQuery.data ? filterPickingListsByShippers(pickingQuery.data, initialSelectedShippers) : []),
    [pickingQuery.data, initialSelectedShippers]
  );

  const count = computePickingPageCount(filteredPickingLists);

  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  if (pickingQuery.isLoading) {
    return <LoadingComponent />;
  }

  if (pickingQuery.isError) {
    return <>Error</>;
  }

  return (
    <Presenter
      models={filteredPickingLists}
      date={date}
      unit={unit}
      barcodeShippers={initialBarcodeShippers}
    />
  );
});

export default PickingListComponent;
